import Axios, { AxiosRequestConfig } from "axios";
import qs from 'qs';
import { ISavedRingConfiguration, IMetalGroup } from "../sharedInterfaces";
import { IMetalZone } from "../Model/RingModel";
import { ICaptureConfig } from "../Tools/Capture/CaptureConfig";

export interface ICheckoutArgument {
  SKU:string,
  fingerSize:string,
  width:number,
  thickness:number,
  metalGroups:IMetalGroup[],
  metalZones:IMetalZone[],
  stoneGroups:{
    name:string,
    stone:'Natural Diamond'|'Lab Grown Diamond'|'Black Diamond'|'Sapphire'|'Ruby',
    stoneSize:number,
    stoneCarat:number
  }[],
  engravingText:string,
  engravingFont:'Brush Script'|'Century Gothic'|'Snell Roundhand'|'Trajan'|'Dakota',
  thumbnail:string,
  additionalData:any
}

export class RequestService{
  private _backendURI:string;
  private _osURI:string;
  private _priceCheckURI:string;
  private _costPriceCheckURI:string;
  private _shortLinkURI:string;

  constructor() {
    this._shortLinkURI = 'https://threed.link';
  }// /constructor()

  public setOsURI = (osURI:string) => {
    this._osURI = osURI;
  }

  public setBackendURI = (beURI:string) => {
    this._backendURI = beURI;
  }

  public setPriceCheckURI = (pcURI:string) => {
    this._priceCheckURI = pcURI;
  }

  public setCostPriceCheckURI = (cpcURI:string) => {
    this._costPriceCheckURI = cpcURI;
  }

  public saveConfiguration = (configuration:ISavedRingConfiguration|ICaptureConfig|ICheckoutArgument|any):Promise<string> => {
    try {
      return Axios.post(`${this._shortLinkURI}/save-design`, {
        design: configuration
      })
      .then((res) => {
        // console.log("saveConfiguration()", res.data);
        return res.data;
      });
    } catch(err) {
      console.error('Error saving configuration!', err);
    };
  }

  public loadConfiguration = (id:string):Promise<ISavedRingConfiguration|ICaptureConfig> => {
    try {
      return Axios.get(`${this._shortLinkURI}/load-design?id=${id}`)
      .then((res) => {
        return res.data;
      });
    } catch(err) {
      console.error('Error loading configuration!', err);
    };
  }

  public getShortLink = (url:string):Promise<string> => {
    try {
      return Axios.post(`${this._shortLinkURI}/short-url`, {
        url: url
      })
      .then((res) => {
        console.log(res.data)
        return res.data.url;
      })
    } catch(err) {
      console.error('Error getting shortLink!', err);
    };
  }

  public fetchRingData = async (SKU?:Array<string>):Promise<any[]> => {
    const config = { params: null };
    if (SKU) config.params = { SKU: SKU }

    try {
      const res = await Axios.get(this._backendURI + '/product/list', config);
      return res.data;
    } catch(err) {
      throw(err);
    };
  }

  public generatePriceCheckConfig = (ring:ICheckoutArgument, token?:string):AxiosRequestConfig => {

    console.log("ring", ring)

    let params = {
      SKU: ring.SKU,
      size: ring.fingerSize,
      fingerSize: ring.fingerSize,
      width: ring.width,
      thickness: ring.thickness,
      metalZones: ring.metalZones,
      stoneGroups: ring.stoneGroups,
      additionalData: ring.additionalData
    }

    let modifyParam = JSON.parse(JSON.stringify(params));
        if(params.metalZones){
          modifyParam.metalZones = params.metalZones.map((e)=>{
            return{
              id:e.id,
              material:e.material + " " + e.purity
            }
        })
    }

    const config:AxiosRequestConfig = {
      params: modifyParam,
      paramsSerializer: (param) => {
        const queryString = qs.stringify(param, { arrayFormat: 'indices', encode: false });
        return queryString;
      },
      headers:{
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      }
    }

    const checkoutConfig = {
      params: params,
      paramsSerializer: (param) => {
        const queryString = qs.stringify(param, { arrayFormat: 'indices', encode: false });
        return queryString;
      }
    }
    if(token){
      return config;      
    }
    else{
      return checkoutConfig;
    }
  };

  public getPrice = async (ring:ICheckoutArgument, token?:string, storeID?:string):Promise<number> => {
    const config = this.generatePriceCheckConfig(ring, token);

    console.log("price", config)

    let url = this._priceCheckURI;
    if(!token){
      if(storeID){
        url = `${this._osURI}/pricePublic/${storeID}`
      }
    }

    try {
      const res = await Axios.get(url, config)
      return Promise.resolve(res.data.price);
    } catch (err) {
      console.error("Error getting price!", err);
      console.warn("For testing/ demonstration purposes we use a random sample price.");
      return Promise.resolve(Math.floor(900 + Math.random() * 1500));
    }
  }

  public getCostPrice = (ring:ICheckoutArgument, token?:string, storeID?:string):Promise<number> => {
    const config = this.generatePriceCheckConfig(ring, token);

    let url = this._costPriceCheckURI;
    if(!token){
      if(storeID){
        url = `${this._osURI}/costPublic/${storeID}`
      }
    }

    return Axios.get(url, config)
    .then((res) => {
      return res.data.price;
    })
    .catch((err) => {
      //Fail silently
      return null;
    });
  };

  public getCheckoutURI = (ring:ICheckoutArgument, token?:string, storeID?:string):Promise<string> => {

    console.log("checkout", token)

    let url = `${this._osURI}/quotes`;
    if(!token){
      url = `${this._osURI}/quotesPublic/${storeID}`
    }

    return Axios.post(url, {
        SKU: ring.SKU,
        size: ring.fingerSize,
        fingerSize: ring.fingerSize,
        width: ring.width,
        thickness: ring.thickness,
        metalGroups: ring.metalGroups,
        metalZones: ring.metalZones.map(e => {return{
          id: e.id,
          material: e.material + " " + e.purity,
          surface: e.surface
        }}),
        stoneGroups: ring.stoneGroups,
        engravingText: ring.engravingText,
        engravingFont: ring.engravingFont,
        thumbnail: ring.thumbnail,
        additionalData: ring.additionalData
    }, {
      headers:{
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      }
    })
    .then((res) => {
      return res.data.url;
    })
    .catch((err) => {
      throw(err);
      //{"url":"http:\/\/qa.infinityorders.com.au\/viscircle\/4"}
    });
  };

  public uploadImg(img:any, storeID:string):Promise<any>{
        return new Promise((res, rej)=>{
                Axios.post(`${this._osURI}/stores/${storeID}/getSignURL`, )
                    .then((result)=>{
                        let url = result.data.url.url;
                        Axios.put(url, img).then(()=>{
                            res("https://infinityorders-new-app.s3.ap-southeast-2.amazonaws.com/" + result.data.filename);
                        }).catch((e)=>{
                            rej(e);
                        })
                    })
                    .catch((e)=>{
                        rej("upload file problem");
                    })
        })
  }

}// /class